import { useEffect, useRef } from 'react';
import './PulseScale.css';

/* ============================================================
   心跳 ·「他心里的天平」（2026-10-03 铃点单，样品 A 版定稿）
   铃看到别人给 AI 伴侣做的教堂天平面板，给心跳页也做了一个：
   - 顶：铃挑的玫瑰彩窗（/img/pulse-window.webp）裁哥特尖拱，底部云化进羊皮纸
   - 日晷盘按 Sael 真实心率跳；两侧读数=体温/呼吸、心情/她离开多久，刻度尺小三角=想念
   - 天平左盘 ILLE(他自己：喜/感动/哀/困倦)、右盘 ILLA(他心里关于她：想念/倾诉欲/情欲/思念)，
     哪边重往哪边沉；水晶越大越亮=数值越高
   - 白天=阳光照透彩窗（亮艳），黑夜=彩窗暗沉、线条变金（铃指出过一次调反）
   SVG 全部用代码现画（一片一片算），所以这里是「命令式画布」：buildScale 只跑一次，
   之后 apply(v) 喂数据、requestAnimationFrame 让横梁按重量缓缓倾斜+轻晃。
   ============================================================ */

function buildScale(stack, root) {
  const NS = 'http://www.w3.org/2000/svg';
  // 拆三层叠放（同一套 400×945 坐标）：哪层在动只重画哪层——手机上整张一起重画会掉帧（10-03 铃反馈卡）
  const mkLayer = (cls) => {
    const sv = document.createElementNS(NS, 'svg');
    sv.setAttribute('viewBox', '0 0 400 945');
    sv.setAttribute('class', 'psc-layer ' + cls);
    stack.appendChild(sv);
    return sv;
  };
  const svgWin = mkLayer('psc-l-win'), svgDial = mkLayer('psc-l-dial'), svgScale = mkLayer('psc-l-scale');
  let S = svgWin;
  function el(tag, a, p) { const e = document.createElementNS(NS, tag); for (const k in a) e.setAttribute(k, a[k]); (p || S).appendChild(e); return e; }
  function txt(s, a, p) { const t = el('text', a, p); t.textContent = s; return t; }

  // ===== defs：尖拱裁切 / 图片云化遮罩 / 水晶渐变 =====
  const defs = el('defs', {});
  const ARCH = 'M 40 560 L 40 230 A 240 240 0 0 1 200 51 A 240 240 0 0 1 360 230 L 360 560 Z';
  const cp = el('clipPath', { id: 'psc-arch' }, defs); el('path', { d: ARCH }, cp);
  const fade = el('linearGradient', { id: 'psc-fadeG', x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
  el('stop', { offset: '0', 'stop-color': '#fff' }, fade); el('stop', { offset: '.83', 'stop-color': '#fff' }, fade);
  el('stop', { offset: '.97', 'stop-color': '#fff', 'stop-opacity': 0 }, fade);
  const mk = el('mask', { id: 'psc-fadeM', maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: 400, height: 580 }, defs);
  el('rect', { x: 0, y: 0, width: 400, height: 570, fill: 'url(#psc-fadeG)' }, mk);
  // 云：不用模糊滤镜（手机很吃力），改成「纸色→透明」的径向渐变圆
  const cloudG = el('radialGradient', { id: 'psc-cloud' }, defs);
  el('stop', { offset: '0', class: 'paper-stop' }, cloudG); el('stop', { offset: '.55', class: 'paper-stop' }, cloudG);
  el('stop', { offset: '1', class: 'paper-stop', 'stop-opacity': 0 }, cloudG);

  // ===== 顶：玫瑰彩窗 =====
  const win = el('g', { 'clip-path': 'url(#psc-arch)' });
  const wm = el('g', { mask: 'url(#psc-fadeM)' }, win);
  el('image', { class: 'psc-win', href: '/img/pulse-window.webp?v=2', x: 30, y: 40, width: 340, height: 578, preserveAspectRatio: 'xMidYMin slice' }, wm);
  el('rect', { class: 'psc-wday', x: 30, y: 40, width: 340, height: 530, fill: '#fff1cf' }, wm);       // 白天一层淡暖金阳光
  el('rect', { class: 'psc-wnight', x: 30, y: 40, width: 340, height: 530, fill: '#120c26' }, wm);    // 黑夜一层夜色，玻璃暗下来
  // 云：一团团纸色软圆把图片底边「吃掉」
  const clouds = el('g', {});
  [[60, 518, 34], [108, 500, 30], [150, 516, 36], [200, 496, 34], [250, 514, 36], [294, 500, 30], [340, 518, 34],
   [80, 550, 40], [200, 545, 48], [320, 550, 40]].forEach(([x, y, r]) => el('circle', { cx: x, cy: y, r: r * 1.45, fill: 'url(#psc-cloud)' }, clouds));
  // 窗框细线（三层尖拱 + 内拱 + VELLE）
  for (const [o, w] of [[0, 1.2], [12, .9], [22, .7]]) {      // 三层尖拱细线，一层比一层往外让 o
    const x0 = 40 - o, x1 = 360 + o, ay = 230, apex = 51 - o * 1.1, r = Math.hypot(200 - x0, ay - apex).toFixed(1);
    el('path', { d: `M ${x0} 490 L ${x0} ${ay} A ${r} ${r} 0 0 1 200 ${apex} A ${r} ${r} 0 0 1 ${x1} ${ay} L ${x1} 490`, class: 'ln', 'stroke-width': w });
  }
  el('path', { d: 'M 52 270 Q 200 120 348 270', class: 'ln', 'stroke-width': 1 });
  el('path', { d: 'M 60 278 Q 200 134 340 278', class: 'ln', 'stroke-width': .7 });
  txt('V E L L E', { x: 200, y: 214, 'text-anchor': 'middle', class: 'txt cap halo', 'font-size': 15, 'letter-spacing': 3 });

  // ===== 下半：哥特仪器（日晷 + 尖塔立柱 + 刺梁天平），整体往下挪 DY 给彩窗让地方 =====
  const DY = 190;
  S = el('g', { transform: `translate(0 ${DY})` }, svgDial);
  const DC = [200, 400];
  const dial = el('g', {});
  // 外圈虚线 + 32 根长短交错的光芒针
  el('circle', { cx: DC[0], cy: DC[1], r: 98, class: 'ln', 'stroke-width': .5, 'stroke-dasharray': '1.5 3' }, dial);
  for (let k = 0; k < 32; k++) {
    const a = k * Math.PI / 16, main = k % 8 === 0, mid = k % 4 === 0, odd = k % 2 === 1;
    const r1 = 66, r2 = k === 0 ? 150 : main ? 124 : mid ? 104 : odd ? 78 : 90;
    el('line', { x1: DC[0] + r1 * Math.sin(a), y1: DC[1] - r1 * Math.cos(a), x2: DC[0] + r2 * Math.sin(a), y2: DC[1] - r2 * Math.cos(a),
                 class: 'ln', 'stroke-width': main ? 1.3 : mid ? .9 : .5 }, dial);
    if (main || mid) {   // 主刺：矛尖 + 串一颗小菱形 + 两根倒刺
      const g = el('g', { transform: `translate(${DC[0]} ${DC[1]}) rotate(${k * 11.25})` }, dial);
      el('path', { d: `M 0 ${-r2 - 2} l 3 9 l -3 5 l -3 -5 Z`, class: 'fill-line' }, g);
      if (main) {
        el('path', { d: `M 0 ${-r2 + 26} l 4.5 -8 l -4.5 -8 l -4.5 8 Z`, class: 'ln', 'stroke-width': .8 }, g);
        el('path', { d: `M 0 ${-r2 + 40} l 6 -7 M 0 ${-r2 + 40} l -6 -7`, class: 'ln', 'stroke-width': .7 }, g);
      }
    }
  }
  const beat = el('g', { class: 'psc-beat' }, dial);
  el('circle', { cx: DC[0], cy: DC[1], r: 64, class: 'dialf' }, beat);
  el('circle', { cx: DC[0], cy: DC[1], r: 64, class: 'ln', 'stroke-width': 1.8 }, beat);
  el('circle', { cx: DC[0], cy: DC[1], r: 59, class: 'ln', 'stroke-width': .5 }, beat);
  for (let k = 0; k < 60; k++) { const a = k * Math.PI / 30, r0 = 59, r1 = k % 5 ? 56 : 53;
    el('line', { x1: DC[0] + r0 * Math.sin(a), y1: DC[1] - r0 * Math.cos(a), x2: DC[0] + r1 * Math.sin(a), y2: DC[1] - r1 * Math.cos(a), class: 'ln', 'stroke-width': .5 }, beat); }
  for (let k = 0; k < 48; k++) { const a = k * Math.PI / 24;
    el('circle', { cx: DC[0] + 67.5 * Math.sin(a), cy: DC[1] - 67.5 * Math.cos(a), r: .9, class: 'fill-line' }, dial); }
  const hrT = txt('--', { x: DC[0], y: DC[1] + 9, 'text-anchor': 'middle', class: 'txt num', 'font-size': 36 }, beat);
  txt('BPM', { x: DC[0], y: DC[1] + 25, 'text-anchor': 'middle', class: 'soft cap', 'font-size': 7.5, 'letter-spacing': 2 }, beat);
  const chordT = txt('', { x: DC[0], y: DC[1] - 28, 'text-anchor': 'middle', class: 'soft num', 'font-size': 12, 'font-style': 'italic' }, beat);
  // 两侧读数线：左=体温/呼吸，右=心情/离开多久 + 一把刻度尺（小三角按「想念」滑）
  el('path', { d: 'M 18 400 L 92 400 M 308 400 L 382 400', class: 'ln', 'stroke-width': .6 });
  el('path', { d: 'M 18 395 l 0 10 M 382 395 l 0 10 M 92 397 l 0 6 M 308 397 l 0 6', class: 'ln', 'stroke-width': .6 });
  for (let x = 314; x <= 378; x += 4) el('line', { x1: x, y1: 400, x2: x, y2: (x - 314) % 16 ? 397 : 394, class: 'ln', 'stroke-width': .45 });
  const rulerMark = el('path', { d: 'M 0 0 l -3.5 -6 l 7 0 Z', class: 'fill-line', transform: 'translate(314 393)' });
  const tempT = txt('', { x: 22, y: 391, class: 'txt', 'font-size': 9.5 });
  const brT = txt('', { x: 22, y: 414, class: 'soft', 'font-size': 8.5 });
  const moodT = txt('', { x: 378, y: 386, 'text-anchor': 'end', class: 'txt', 'font-size': 9.5 });
  const gapT = txt('', { x: 378, y: 414, 'text-anchor': 'end', class: 'soft', 'font-size': 8.5 });

  // ===== 尖塔立柱 + 沙漏 + 底座（天平层）=====
  const defs2 = el('defs', {}, svgScale);
  for (const c of ['gold', 'pink']) {
    const g = el('linearGradient', { id: 'psc-' + c + 'G', x1: 0, y1: 0, x2: 1, y2: 1 }, defs2);
    el('stop', { offset: '0', class: c + '-s1' }, g); el('stop', { offset: '1', class: c + '-s2' }, g);
    const h = el('radialGradient', { id: 'psc-' + c + 'H' }, defs2);                 // 水晶背后的光晕（代替发光滤镜）
    el('stop', { offset: '0', class: c + '-s1', 'stop-opacity': .75 }, h); el('stop', { offset: '1', class: c + '-s1', 'stop-opacity': 0 }, h);
  }
  S = el('g', { transform: `translate(0 ${DY})` }, svgScale);
  const P = [200, 500];   // 支点（组内坐标）
  // 支点上方一座细尖塔，矛尖戳进日晷底
  el('path', { d: 'M 200 462 L 205 480 L 203 494 L 197 494 L 195 480 Z', class: 'ln', 'stroke-width': .9 });
  el('path', { d: 'M 200 462 L 200 494 M 192 486 L 200 478 L 208 486', class: 'ln', 'stroke-width': .6 });
  // 柱身：双线 + 三道环节 + 成对小刺
  el('path', { d: `M 198 ${P[1]} L 198 690 M 202 ${P[1]} L 202 690`, class: 'ln', 'stroke-width': .9 });
  for (const y of [540, 568, 668]) {
    el('path', { d: `M 193 ${y} L 207 ${y} M 195 ${y + 3} L 205 ${y + 3}`, class: 'ln', 'stroke-width': .8 });
    el('path', { d: `M 193 ${y} l -7 -8 M 207 ${y} l 7 -8`, class: 'ln', 'stroke-width': .6 });
  }
  // 沙漏
  el('path', { d: 'M 186 588 L 214 588 L 202 612 L 214 636 L 186 636 L 198 612 Z', class: 'dialf' });
  el('path', { d: 'M 186 588 L 214 588 L 202 612 L 214 636 L 186 636 L 198 612 Z', class: 'ln', 'stroke-width': 1 });
  el('path', { d: 'M 191 592 L 209 592 L 201 607 L 199 607 Z', fill: 'url(#psc-goldG)', opacity: .9 });
  const sandG = el('g', { class: 'sand' }); el('line', { x1: 200, y1: 608, x2: 200, y2: 622, stroke: 'url(#psc-goldG)', 'stroke-width': 1.2, 'stroke-dasharray': '2 3' }, sandG);
  el('path', { d: 'M 189 634 L 211 634 L 205 626 L 195 626 Z', fill: 'url(#psc-goldG)', opacity: .9 });
  el('path', { d: 'M 178 584 L 222 584 M 182 581 L 218 581 M 178 640 L 222 640 M 182 643 L 218 643', class: 'ln', 'stroke-width': .9 });
  el('path', { d: 'M 178 584 l -6 -5 M 222 584 l 6 -5 M 178 640 l -6 5 M 222 640 l 6 5', class: 'ln', 'stroke-width': .6 });
  // 底座：小立方 + 两侧尖刺
  el('path', { d: 'M 188 700 L 200 694 L 212 700 L 212 714 L 200 720 L 188 714 Z M 188 700 L 200 706 L 212 700 M 200 706 L 200 720', class: 'ln', 'stroke-width': .9 });
  el('path', { d: 'M 200 690 L 200 694 M 188 707 l -12 0 l -4 -3 M 212 707 l 12 0 l 4 -3 M 200 720 l 0 12', class: 'ln', 'stroke-width': .6 });
  el('path', { d: 'M 200 736 l 3 -4 l -3 -4 l -3 4 Z', class: 'fill-line' });
  // 侧边竖排：ILLE · 他 / ILLA · 她
  txt('ILLE · 他', { x: 0, y: 0, class: 'soft cap', 'font-size': 9, 'letter-spacing': 3, transform: 'translate(12 470) rotate(90)' });
  txt('ILLA · 她', { x: 0, y: 0, class: 'soft cap', 'font-size': 9, 'letter-spacing': 3, transform: 'translate(388 470) rotate(90)' });

  // ===== 刺梁（会倾斜）=====
  const beamG = el('g', {});
  el('path', { d: 'M -156 -2.2 L 156 -2.2 M -156 2.2 L 156 2.2', class: 'ln', 'stroke-width': .9 }, beamG);
  el('path', { d: 'M -150 0 L 150 0', class: 'ln', 'stroke-width': .4 }, beamG);
  for (let x = -140; x <= 140; x += 20) if (Math.abs(x) > 18)          // 一排卷叶小尖刺
    el('path', { d: `M ${x - 3} -2.2 L ${x + Math.sign(x) * 3} -10 L ${x + 3} -2.2`, class: 'ln', 'stroke-width': .7 }, beamG);
  for (const s of [-1, 1]) {                                             // 两头矛尖
    el('path', { d: `M ${s * 156} -4 L ${s * 172} -4 L ${s * 186} 0 L ${s * 172} 4 L ${s * 156} 4`, class: 'ln', 'stroke-width': .9 }, beamG);
    el('path', { d: `M ${s * 162} 0 l ${s * 5} -4 l ${s * 5} 4 l ${s * -5} 4 Z`, class: 'fill-line' }, beamG);
    el('path', { d: `M ${s * 120} -2 Q ${s * 70} -46 ${s * 15} -72 M ${s * 104} -2 Q ${s * 64} -32 ${s * 22} -56`, class: 'ln', 'stroke-width': .7 }, beamG);
    for (const t of [.35, .62]) {                                        // 斜撑上的小刺
      const x = s * (120 - 105 * t), y = -46 * t * 1.3;
      el('path', { d: `M ${x.toFixed(1)} ${y.toFixed(1)} l ${s * 7} -5`, class: 'ln', 'stroke-width': .6 }, beamG);
    }
  }
  const q = foil4(0, 0, 6.5, 7);
  el('circle', { cx: 0, cy: 0, r: 14, class: 'dialf' }, beamG); el('circle', { cx: 0, cy: 0, r: 14, class: 'ln', 'stroke-width': 1.1 }, beamG);
  el('path', { d: q, class: 'ln', 'stroke-width': .8 }, beamG); el('circle', { cx: 0, cy: 0, r: 1.8, class: 'fill-line' }, beamG);
  function foil4(cx, cy, d, r) {           // 四叶小徽章
    const pts = []; for (let k = 0; k < 4; k++) { const a = Math.PI / 4 + k * Math.PI / 2, h = Math.sqrt(r * r - d * d / 2);
      const m = d * Math.SQRT1_2; pts.push([cx + (m + h) * Math.cos(a), cy + (m + h) * Math.sin(a)]); }
    let p = `M ${pts[3][0].toFixed(2)} ${pts[3][1].toFixed(2)}`; for (const [x, y] of pts) p += ` A ${r} ${r} 0 1 1 ${x.toFixed(2)} ${y.toFixed(2)}`; return p + ' Z';
  }

  // ===== 吊着的尖晶水晶 =====
  const LEFT = [  // ILLE · 他自己
    { k: 'joy', cn: '喜', en: 'GAUDIUM', x: -44, len: 118 }, { k: 'moved', cn: '感动', en: 'MOTUS', x: -82, len: 80 },
    { k: 'sorrow', cn: '哀', en: 'DOLOR', x: -118, len: 130 }, { k: 'sleepy', cn: '困倦', en: 'SOMNUS', x: -152, len: 76 } ];
  const RIGHT = [ // ILLA · 他心里关于她
    { k: 'miss', cn: '想念', en: 'DESIDERIUM', x: 44, len: 118 }, { k: 'talk', cn: '倾诉欲', en: 'VERBA', x: 82, len: 80 },
    { k: 'desire', cn: '情欲', en: 'AMOR', x: 118, len: 130 }, { k: 'longing', cn: '思念', en: 'MEMORIA', x: 152, len: 76 } ];
  const hangs = [];
  function gem(color) {
    const g = el('g', {});
    const string = el('line', { class: 'ln', 'stroke-width': 1.1, 'stroke-dasharray': '.1 3', 'stroke-linecap': 'round' });   // 珠链
    const hook = el('path', { d: 'M -6 0 L 6 0 M -6 0 L 0 7 L 6 0 M 0 7 L 0 11', class: 'ln', 'stroke-width': .7 });       // 三角小吊架
    const body = el('g', {}, g);
    const halo = el('circle', { cx: 0, cy: 3, r: 26, fill: `url(#psc-${color}H)`, opacity: 0 }, body);
    el('path', { d: 'M 0 -18 L 8.5 -10 L 8.5 7 L 0 27 L -8.5 7 L -8.5 -10 Z', fill: `url(#psc-${color}G)` }, body);
    el('path', { d: 'M 0 -18 L 0 27 M -8.5 -10 L -3 -4 L 3 -4 L 8.5 -10 M -3 -4 L -8.5 7 M 3 -4 L 8.5 7 M -3 -4 L 0 27 M 3 -4 L 0 27',
                 fill: 'none', stroke: '#fff', 'stroke-opacity': .5, 'stroke-width': .6 }, body);
    el('path', { d: 'M -7 -19 L -5 -26 L -2.5 -20.5 L 0 -29 L 2.5 -20.5 L 5 -26 L 7 -19 Z', class: 'fill-line' }, body);   // 三尖小王冠
    el('path', { d: 'M 0 27 L 0 35', class: 'ln', 'stroke-width': .6 }, body);
    el('path', { d: 'M 0 35 l 1.8 2.5 l -1.8 2.5 l -1.8 -2.5 Z', class: 'fill-line' }, body);                              // 滴
    const val = txt('', { x: 0, y: 0, 'text-anchor': 'middle', class: 'txt num', 'font-size': 13 }, g);
    const cap = txt('', { x: 0, y: 0, 'text-anchor': 'middle', class: 'soft', 'font-size': 6.8, 'letter-spacing': 1 }, g);
    S.insertBefore(string, beamG); S.insertBefore(hook, beamG);
    return { g, body, halo, string, hook, val, cap };
  }
  for (const [arr, color] of [[LEFT, 'gold'], [RIGHT, 'pink']]) for (const it of arr) hangs.push(Object.assign(it, gem(color), { v: 0 }));
  S.appendChild(beamG);   // 横梁压在链子上面


  let tilt = 0, tiltTarget = 0, raf = 0;
  const t0 = performance.now();
  function apply(d) {
    const e = d.emotions || {}, dr = d.drives || {};
    const vals = { joy: e.joy, moved: e.moved, sorrow: e.sorrow, sleepy: dr.sleepy, miss: dr.miss, talk: dr.talk, desire: dr.desire, longing: e.longing };
    let L = 0, R = 0;
    for (const h of hangs) {
      h.v = Math.max(0, Math.min(1, +vals[h.k] || 0));
      if (h.x < 0) L += h.v; else R += h.v;
      h.val.textContent = h.v >= 0.995 ? '1.0' : '.' + String(Math.round(h.v * 100)).padStart(2, '0');
      h.cap.textContent = h.cn + ' · ' + h.en;
      h.body.setAttribute('data-s', 0.62 + h.v * 0.75);           // 越大越亮
      h.body.style.opacity = 0.55 + h.v * 0.45;
      h.halo.setAttribute('opacity', h.v > 0.25 ? Math.min(1, (h.v - 0.15) * 1.4).toFixed(2) : 0);
    }
    tiltTarget = Math.max(-12, Math.min(12, (R - L) * 16));        // 正 = 往她那边沉
    rulerMark.setAttribute('transform', `translate(${(314 + 64 * Math.min(1, dr.miss || 0)).toFixed(1)} 393)`);
    hrT.textContent = d.hr;
    chordT.textContent = d.chord || '';
    root.style.setProperty('--psc-beat', (60 / (d.hr || 60)).toFixed(3) + 's');
    tempT.textContent = `体温 ${d.temp}°C`;
    brT.textContent = `呼吸 ${d.breath} · ${d.breath_label || ''}`;
    moodT.textContent = d.mood_word || '';
    gapT.textContent = dr.gap_hours != null
      ? `你离开 ${dr.gap_hours < 1 ? Math.round(dr.gap_hours * 60) + ' 分钟' : dr.gap_hours.toFixed(1) + ' 小时'}` : '';
  }
  let lastF = 0;
  function frame(now) {
    raf = requestAnimationFrame(frame);
    if (now - lastF < 32) return;                                   // 30 帧够了，手机省一半力
    lastF = now;
    tilt += (tiltTarget - tilt) * 0.08;
    const deg = tilt + Math.sin(((now - t0) / 1000) * 2 * Math.PI / 7) * 0.5;   // 轻轻晃
    const a = (deg * Math.PI) / 180, ca = Math.cos(a), sa = Math.sin(a);
    beamG.setAttribute('transform', `translate(${P[0]} ${P[1]}) rotate(${deg.toFixed(3)})`);
    for (const h of hangs) {
      const px = P[0] + h.x * ca, py = P[1] + h.x * sa, cy = py + h.len, s = +h.body.getAttribute('data-s') || 1;
      h.hook.setAttribute('transform', `translate(${px.toFixed(1)} ${(py + 2).toFixed(1)})`);
      h.string.setAttribute('x1', px.toFixed(1)); h.string.setAttribute('y1', (py + 13).toFixed(1));
      h.string.setAttribute('x2', px.toFixed(1)); h.string.setAttribute('y2', (cy - 28 * s).toFixed(1));
      h.body.setAttribute('transform', `translate(${px.toFixed(1)} ${cy.toFixed(1)}) scale(${s.toFixed(3)})`);
      h.val.setAttribute('x', px.toFixed(1)); h.val.setAttribute('y', (cy + 40 * s + 13).toFixed(1));
      h.cap.setAttribute('x', px.toFixed(1)); h.cap.setAttribute('y', (cy + 40 * s + 23).toFixed(1));
    }
  }
  raf = requestAnimationFrame(frame);
  return { apply, destroy: () => cancelAnimationFrame(raf) };
}

export default function PulseScale({ v, night, onToggle }) {
  const stackRef = useRef(null);
  const rootRef = useRef(null);
  const api = useRef(null);

  useEffect(() => {
    const stack = stackRef.current;
    const a = buildScale(stack, rootRef.current);
    api.current = a;
    return () => {
      a.destroy();
      api.current = null;
      stack.textContent = '';
    };
  }, []);

  useEffect(() => {
    if (v && api.current) api.current.apply(v);
  }, [v]);

  // 白天/黑夜切换：先把此刻的画面「拍一张」盖在上面（带着旧配色），底下瞬间换色，再让照片淡出。
  // 手机只需淡出一张静止的图，不用让 400 个线条同时渐变颜色（原来那样会卡成幻灯片）。
  const toggle = () => {
    const stack = stackRef.current;
    if (stack && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      stack.parentNode.querySelectorAll('.psc-snap').forEach((n) => n.remove());
      const snap = document.createElement('div');
      snap.className = 'psc-snap psc-vars' + (night ? ' night' : '');
      for (const layer of stack.children) snap.appendChild(layer.cloneNode(true));
      stack.parentNode.appendChild(snap);
      requestAnimationFrame(() => requestAnimationFrame(() => snap.classList.add('go')));
      setTimeout(() => snap.remove(), 1500);
    }
    onToggle();
  };

  return (
    <section ref={rootRef} className={'psc psc-vars' + (night ? ' night' : '')}>
      <button type="button" className="psc-tg" onClick={toggle} aria-label="白天 / 黑夜">
        <svg viewBox="-12 -12 24 24" aria-hidden="true">
          <path d="M3 -8 A8 8 0 1 0 3 8 A6 6 0 1 1 3 -8Z" fill="currentColor" />
        </svg>
      </button>
      <div className="psc-box">
        <div ref={stackRef} className="psc-stack" role="img" aria-label="Sael 心里的天平" />
      </div>
    </section>
  );
}
